import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const StakeholdersSection = ({ data, onChange, errors }) => {
  const [newStakeholder, setNewStakeholder] = useState({
    name: '',
    role: '',
    department: '',
    email: '',
    phone: '',
    involvement: ''
  });

  const involvementOptions = [
    { value: 'primary', label: 'Primary Contact - Daily involvement' },
    { value: 'secondary', label: 'Secondary Contact - Weekly check-ins' },
    { value: 'reviewer', label: 'Reviewer - Milestone reviews only' },
    { value: 'advisor', label: 'Advisor - As needed consultation' }
  ];

  const communicationPreferences = [
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'video', label: 'Video Calls' },
    { value: 'in-person', label: 'In-Person Meetings' },
    { value: 'slack', label: 'Slack/Teams' }
  ];

  const addStakeholder = () => {
    if (newStakeholder?.name && newStakeholder?.email && newStakeholder?.involvement) {
      const stakeholders = data?.stakeholders || [];
      onChange('stakeholders', [...stakeholders, { ...newStakeholder, id: Date.now() }]);
      setNewStakeholder({
        name: '',
        role: '',
        department: '',
        email: '',
        phone: '',
        involvement: ''
      });
    }
  };

  const removeStakeholder = (id) => {
    const stakeholders = data?.stakeholders || [];
    onChange('stakeholders', stakeholders?.filter(s => s?.id !== id));
  };

  const updateStakeholder = (id, field, value) => {
    const stakeholders = data?.stakeholders || [];
    onChange('stakeholders', stakeholders?.map(s => 
      s?.id === id ? { ...s, [field]: value } : s
    ));
  };

  const handleCommunicationPreferenceChange = (preference, checked) => {
    const preferences = data?.communicationPreferences || [];
    if (checked) {
      onChange('communicationPreferences', [...preferences, preference]);
    } else {
      onChange('communicationPreferences', preferences?.filter(p => p !== preference));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
          <Icon name="Users" size={16} color="white" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-foreground">Stakeholders & Contacts</h2>
          <p className="text-sm text-muted-foreground">Identify key people involved in this project</p>
        </div>
      </div>
      {/* Primary Point of Contact */}
      <div className="bg-muted rounded-lg p-4 space-y-4">
        <h3 className="text-lg font-medium text-foreground flex items-center space-x-2">
          <Icon name="Star" size={16} className="text-accent" />
          <span>Primary Point of Contact</span>
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Full Name"
            type="text"
            placeholder="Enter primary contact name"
            value={data?.primaryContact?.name || ''}
            onChange={(e) => onChange('primaryContact', { ...data?.primaryContact, name: e?.target?.value })}
            error={errors?.primaryContactName}
            required
          />

          <Input
            label="Job Title"
            type="text"
            placeholder="e.g., City Planning Director"
            value={data?.primaryContact?.title || ''}
            onChange={(e) => onChange('primaryContact', { ...data?.primaryContact, title: e?.target?.value })}
            required
          />

          <Input
            label="Department"
            type="text"
            placeholder="e.g., Urban Development"
            value={data?.primaryContact?.department || ''}
            onChange={(e) => onChange('primaryContact', { ...data?.primaryContact, department: e?.target?.value })}
          />

          <Input
            label="Email Address"
            type="email"
            placeholder="contact@city.gov"
            value={data?.primaryContact?.email || ''}
            onChange={(e) => onChange('primaryContact', { ...data?.primaryContact, email: e?.target?.value })}
            error={errors?.primaryContactEmail}
            required
          />

          <Input
            label="Phone Number"
            type="tel"
            placeholder="(555) 123-4567"
            value={data?.primaryContact?.phone || ''}
            onChange={(e) => onChange('primaryContact', { ...data?.primaryContact, phone: e?.target?.value })}
          />

          <Select
            label="Availability"
            placeholder="Select availability"
            options={[
              { value: 'daily', label: 'Daily (High involvement)' },
              { value: 'weekly', label: 'Weekly check-ins' },
              { value: 'biweekly', label: 'Bi-weekly meetings' },
              { value: 'monthly', label: 'Monthly reviews' }
            ]}
            value={data?.primaryContact?.availability || ''}
            onChange={(value) => onChange('primaryContact', { ...data?.primaryContact, availability: value })}
          />
        </div>
      </div>
      {/* Additional Stakeholders */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground">Additional Stakeholders</h3>
        
        {/* Add New Stakeholder Form */}
        <div className="border border-border rounded-lg p-4 space-y-4">
          <h4 className="font-medium text-foreground">Add Stakeholder</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Name"
              type="text"
              placeholder="Stakeholder name"
              value={newStakeholder?.name}
              onChange={(e) => setNewStakeholder({ ...newStakeholder, name: e?.target?.value })}
            />

            <Input
              label="Role/Title"
              type="text"
              placeholder="Job title"
              value={newStakeholder?.role}
              onChange={(e) => setNewStakeholder({ ...newStakeholder, role: e?.target?.value })}
            />

            <Input
              label="Department"
              type="text"
              placeholder="Department"
              value={newStakeholder?.department}
              onChange={(e) => setNewStakeholder({ ...newStakeholder, department: e?.target?.value })}
            />

            <Input
              label="Email"
              type="email"
              placeholder="email@city.gov"
              value={newStakeholder?.email}
              onChange={(e) => setNewStakeholder({ ...newStakeholder, email: e?.target?.value })}
            />

            <Input
              label="Phone"
              type="tel"
              placeholder="Phone number"
              value={newStakeholder?.phone}
              onChange={(e) => setNewStakeholder({ ...newStakeholder, phone: e?.target?.value })}
            />

            <Select
              label="Involvement Level"
              placeholder="Select involvement"
              options={involvementOptions}
              value={newStakeholder?.involvement}
              onChange={(value) => setNewStakeholder({ ...newStakeholder, involvement: value })}
            />
          </div>

          <Button
            variant="outline"
            onClick={addStakeholder}
            disabled={!newStakeholder?.name || !newStakeholder?.email || !newStakeholder?.involvement}
            iconName="Plus"
            iconPosition="left"
            iconSize={16}
          >
            Add Stakeholder
          </Button>
        </div>

        {/* Existing Stakeholders List */}
        {data?.stakeholders && data?.stakeholders?.length > 0 && (
          <div className="space-y-3">
            {data?.stakeholders?.map((stakeholder) => (
              <div key={stakeholder?.id} className="border border-border rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="font-medium text-foreground">{stakeholder?.name}</p>
                      <p className="text-sm text-muted-foreground">{stakeholder?.role}</p>
                      {stakeholder?.department && (
                        <p className="text-xs text-muted-foreground">{stakeholder?.department}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-foreground">{stakeholder?.email}</p>
                      {stakeholder?.phone && (
                        <p className="text-sm text-muted-foreground">{stakeholder?.phone}</p>
                      )}
                    </div>
                    <div>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-primary/10 text-primary">
                        {involvementOptions?.find(opt => opt?.value === stakeholder?.involvement)?.label?.split(' - ')?.[0]}
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeStakeholder(stakeholder?.id)}
                    iconName="Trash2"
                    iconSize={16}
                    className="text-muted-foreground hover:text-error"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Communication Preferences */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground">Communication Preferences</h3>
        <p className="text-sm text-muted-foreground">
          Select preferred communication methods for project updates and meetings
        </p>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {communicationPreferences?.map((preference) => (
            <label key={preference?.value} className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={(data?.communicationPreferences || [])?.includes(preference?.value)}
                onChange={(e) => handleCommunicationPreferenceChange(preference?.value, e?.target?.checked)}
                className="w-4 h-4 text-primary bg-input border-border rounded focus:ring-ring focus:ring-2"
              />
              <span className="text-sm text-foreground">{preference?.label}</span>
            </label>
          ))}
        </div>
      </div>
      {/* Meeting Preferences */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Select
          label="Preferred Meeting Frequency"
          placeholder="Select frequency"
          options={[
            { value: 'weekly', label: 'Weekly' },
            { value: 'biweekly', label: 'Bi-weekly' },
            { value: 'monthly', label: 'Monthly' },
            { value: 'milestone', label: 'Milestone-based' },
            { value: 'as-needed', label: 'As needed' }
          ]}
          value={data?.meetingFrequency || ''}
          onChange={(value) => onChange('meetingFrequency', value)}
        />

        <Input
          label="Preferred Meeting Time"
          type="text"
          placeholder="e.g., Tuesdays 2-3 PM EST"
          value={data?.preferredMeetingTime || ''}
          onChange={(e) => onChange('preferredMeetingTime', e?.target?.value)}
          description="Specify preferred days and times"
        />
      </div>
    </div>
  );
};

export default StakeholdersSection;